class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // 启动 DOM 雷达监控
    this.initCartObserver();
  }

  // --- 🦾 终极物理免疫：强制还原并斩断原厂篡改链 ---
  lazyLoadImages() {
    const images = this.querySelectorAll('[id^="CartDrawer"] img, .cart-item img, .drawer__inner img');
    
    images.forEach(img => {
      // 1. 提取备份源
      const realSrc = img.getAttribute('data-src') || img.dataset.src;
      const realSrcset = img.getAttribute('data-srcset') || img.dataset.srcset;

      // 2. 如果存在真实路径且当前 src 被篡改成了透明图/空图，执行铁腕还原
      if (realSrc && (!img.src || img.src.startsWith('data:image') || img.src !== realSrc)) {
        
        // 强行赋予正确的值
        img.src = realSrc;
        if (realSrcset) img.srcset = realSrcset;

        // 3. 🔥 绝杀：移除所有 data- 懒加载标记，让原厂懒加载插件找不到目标，直接废掉它的二次覆盖能力
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('loading');

        // 4. 强制打上完工标签，破除 css 隐藏
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded', 'loaded');
        img.style.setProperty('opacity', '1', 'important');
        img.style.setProperty('visibility', 'visible', 'important');

        // 5. 🔒 属性锁：防止原厂脚本后续通过 img.src = xxx 再次篡改
        try {
          Object.defineProperty(img, 'src', {
            writable: false,
            configurable: true
          });
          if (realSrcset) {
            Object.defineProperty(img, 'srcset', {
              writable: false,
              configurable: true
            });
          }
        } catch (e) {
          // 防御个别浏览器严格模式下的报错
        }
      }
    });
  }

  // 雷达监听器
  initCartObserver() {
    const targetNode = this.querySelector('.drawer__inner') || this;
    if (!targetNode) return;

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' || mutation.type === 'subtree') {
          this.lazyLoadImages();
        }
      }
    });

    observer.observe(targetNode, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    this.lazyLoadImages();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (this.activeElement) removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    
    this.productId = parsedState.id;
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
        
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    const triggerRefresh = () => {
      this.lazyLoadImages();
      
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
    };

    requestAnimationFrame(() => {
      triggerRefresh();
      this.open();
    });

    setTimeout(triggerRefresh, 50);
    setTimeout(triggerRefresh, 150);
    setTimeout(triggerRefresh, 400); // 彻底锁死原厂延迟动效后的二次洗牌
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  renderContents(parsedState) {
    super.renderContents(parsedState);
    
    const runLazy = () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.lazyLoadImages === 'function') {
        drawer.lazyLoadImages();
      }
    };

    setTimeout(runLazy, 50);
    setTimeout(runLazy, 200);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);